import { MaterialReactTable } from 'material-react-table'
import { useAdminMaterialReactTable } from '../../../../components/table/mui-table'
import type { FC } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import { OrganizationCell } from './organization-cell'
import type { Stores } from '../../../../util/store'
import { useOrganizationDataGrants } from '../../../../stores/organization-data-grants-store'
import { GrantedByCell } from './granted-by-cell'
import type { DateTime } from 'luxon'
import { formatDate } from '../../../../util/format'
import { DeleteButton } from '../../../../components/delete-button'

export interface OrganizationGrantsPageOptions {
    stores: Stores
}

export const OrganizationGrantsPage: FC<OrganizationGrantsPageOptions> =
    observer(({ stores }) => {
        const { organizationId } = useParams()

        const grants = useOrganizationDataGrants(
            stores.organizationDataGrants,
            organizationId,
        )

        const table = useAdminMaterialReactTable(
            {
                columns: [
                    {
                        accessorKey: 'id',
                        header: 'ID',
                        Cell: ({ cell }) => {
                            const inputId = cell.getValue<string>()
                            return <>{inputId}</>
                        },
                    },
                    {
                        accessorKey: 'provider_organization_id',
                        header: 'From',
                        Cell: ({ cell }) => {
                            return (
                                <OrganizationCell cell={cell} stores={stores} />
                            )
                        },
                    },
                    {
                        accessorKey: 'consumer_organization_id',
                        header: 'To',
                        Cell: ({ cell }) => {
                            return (
                                <OrganizationCell cell={cell} stores={stores} />
                            )
                        },
                    },
                    {
                        accessorKey: 'created_at',
                        header: 'Since',
                        Cell: ({ cell }) => {
                            const createdAt = cell.getValue<DateTime>()
                            return formatDate(createdAt)
                        },
                    },
                    {
                        accessorKey: 'granted_by_user_id',
                        header: 'Granted by',
                        Cell: ({ cell }) => {
                            return <GrantedByCell cell={cell} stores={stores} />
                        },
                    },
                    {
                        accessorKey: 'notes',
                        header: 'Notes',
                        Cell: ({ cell }) => {
                            const notes = cell.getValue<string>()
                            return <p style={{ whiteSpace: 'wrap' }}>{notes}</p>
                        },
                    },
                    {
                        header: 'Actions',
                        Cell: ({ row }) => {
                            return (
                                <DeleteButton
                                    onDelete={() => {
                                        const id = row.getValue<string>('id')
                                        stores.organizationDataGrants
                                            .delete(id)
                                            .catch(() => {}) // no-op, handled in store
                                    }}
                                />
                            )
                        },
                    },
                ],
                data: grants,
                defaultColumn: {
                    enableColumnFilter: true,
                    enableSorting: true,
                },
                getRowId: (grant) => grant.id,
                enableSorting: true,
                createDisplayMode: 'row',
                enableRowSelection: false,
                state: {
                    isLoading: false,
                    showProgressBars: false,
                    columnVisibility: {
                        id: false,
                    },
                },
            },
            'grants-table',
        )

        return (
            <div data-testid="org-data-grants-page" style={{ marginTop: '1rem' }}>
                <MaterialReactTable table={table} />
            </div>
        )
    })
